/**
 * The current impact estimate, shared between the panel that requests it and
 * the map that draws it.
 *
 * Kept tiny and separate: this is not telemetry, it changes only when a
 * commander asks a question, and both the SEARCH panel and the map ring layer
 * read the same array so they can never show different search areas.
 */

import { create } from 'zustand';

export type ImpactScenario = 'loss' | 'return' | 'continue';
export type ImpactLikelihood = 'primary' | 'secondary' | 'possible';

export interface ImpactEstimate {
  scenario: ImpactScenario;
  likelihood: ImpactLikelihood;
  lat: number;
  lon: number;
  radiusM: number;
  alongM: number;
  crossM: number;
  axisBearingDeg: number;
  fallTimeS: number;
  rationale: string;
}

export interface ImpactContour {
  /** 0.5, 0.9 — the share of simulated impacts inside this ring. */
  fraction: number;
  lat: number;
  lon: number;
  radiusM: number;
}

export interface ImpactHeatmap {
  centroidLat: number;
  centroidLon: number;
  samples: number;
  /** A representative subsample of the impact cloud, for drawing density. */
  points: Array<{ lat: number; lon: number }>;
  contours: ImpactContour[];
}

export interface ImpactResult {
  droneId: string;
  cause: string;
  windKnown: boolean;
  rangeRemainingM: number | null;
  lastKnown: {
    lat: number;
    lon: number;
    altM: number;
    headingDeg: number | null;
    groundSpeedMs: number | null;
    kind: string;
    fromSnapshot: boolean;
  };
  estimates: ImpactEstimate[];
  /** The Monte Carlo cloud for the falling case. Null when it was turned off. */
  heatmap: ImpactHeatmap | null;
}

interface ImpactState {
  result: ImpactResult | null;
  setResult: (result: ImpactResult | null) => void;
  clear: () => void;
}

export const useImpactStore = create<ImpactState>((set) => ({
  result: null,
  setResult: (result) => set({ result }),
  clear: () => set({ result: null }),
}));
